import Vue from 'vue';
import Router from 'vue-router';

// Route modules
import authRoutes from './routes/auth.routes';
import membersRoutes from './routes/members.routes';
import membershipsRoutes from './routes/memberships.routes';
import requestsRoutes from './routes/requests.routes';
import membershipTypesRoutes from './routes/membership-types.routes';
import trainersRoutes from './routes/trainers.routes';
import professionalsRoutes from './routes/professionals.routes';
import financeRoutes from './routes/finance.routes';
import scheduleRoutes from './routes/schedule.routes';
import reportsRoutes from './routes/reports.routes';
import systemSettingsRoutes from './routes/system-settings.routes';
import vendorsRoutes from './routes/vendors.routes';
import tasksRoutes from './routes/tasks.routes';
import miscRoutes from './routes/misc.routes';
import helpRoutes from './routes/help.routes';
import whatsNewRoutes from './routes/whats-new.routes';

// Main dashboard layout (eager — always needed)
const MainDashboard = () => import('@/views/MainDashboard');

Vue.use(Router);

// Read at navigation time, not module load: the cookie plugin is registered in main.js
// before the root instance mounts, which is what triggers the first transition.
const hasSession = () => {
  try {
    return !!(Vue.$cookies && Vue.$cookies.get('userData'));
  } catch (err) {
    return false;
  }
};

// Swallow expected navigation-failure rejections from $router.push / $router.replace.
// vue-router 3.x rejects on duplicated/cancelled/aborted/redirected navigations
// (same location, interrupted by a newer push, guard returned false, guard redirected).
// All four are normal user-driven outcomes, not real errors.
const EXPECTED_NAV_FAILURE_NAMES = new Set([
  'NavigationDuplicated',
  'NavigationCancelled',
  'NavigationAborted',
  'NavigationRedirected',
]);
const isExpectedNavFailure = (err) => {
  if (!err) return false;
  if (Router.isNavigationFailure && Router.isNavigationFailure(err)) return true;
  return EXPECTED_NAV_FAILURE_NAMES.has(err.name);
};
const suppressNavFailures = (originalFn) => function patchedNav(location, onComplete, onAbort) {
  if (onComplete || onAbort) {
    return originalFn.call(this, location, onComplete, onAbort);
  }
  return originalFn.call(this, location).catch((err) => {
    if (isExpectedNavFailure(err)) return err;
    throw err;
  });
};
Router.prototype.push = suppressNavFailures(Router.prototype.push);
Router.prototype.replace = suppressNavFailures(Router.prototype.replace);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    // vue-router strips the trailing slash off a child's `path: '/'` and then joins it to the
    // parent, so the login form written that way registers at '/login/' and the dashboard at
    // '/dashboard/'. Nothing was left at the bare root: typing the domain matched no route and
    // painted an empty page. Every in-app link uses names, which is why only a typed URL hit it.
    {
      path: '/',
      redirect: () => (hasSession() ? { name: 'dashboard' } : { name: 'login-form' }),
    },
    authRoutes,
    {
      path: '/dashboard',
      component: MainDashboard,
      children: [
        // Misc routes (dashboard, leads, POS, reception, search, settings, catch-all)
        ...miscRoutes,

        // Feature route modules
        membersRoutes,
        membershipsRoutes,
        requestsRoutes,
        membershipTypesRoutes,
        trainersRoutes,
        professionalsRoutes,
        financeRoutes,
        scheduleRoutes,
        reportsRoutes,
        ...helpRoutes,
        ...whatsNewRoutes,
        systemSettingsRoutes,
        vendorsRoutes,
        tasksRoutes,
      ],
    },
    // The only other catch-all is a child of /dashboard, so it never covered a mistyped
    // top-level URL -- those matched nothing and painted the same empty page the bare root
    // did. Sent to the root, which already knows whether this visitor belongs on the
    // dashboard or at the login form. Last in the list, so it shadows nothing above it.
    {
      path: '*',
      redirect: '/',
    },
  ],
});

// Recover from stale-deploy chunk failures: when a route's JS/CSS chunk no
// longer exists (filename changed in a new release), reload once so the user
// picks up the new asset manifest. The session flag prevents reload loops if
// the chunk is genuinely missing or blocked.
const CHUNK_RELOAD_FLAG = 'chunk-reload-attempted';
router.onError((error) => {
  const message = (error && error.message) || '';
  const isChunkError = (error && error.name === 'ChunkLoadError')
    || /Loading (CSS )?chunk \S+ failed/i.test(message);
  if (!isChunkError) return;
  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return;
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
  } catch (_) {
    // sessionStorage may be unavailable (private mode, etc.) — fall through
    // and reload anyway; a single stale-asset failure is preferable to a stuck app.
  }
  window.location.reload();
});
router.afterEach(() => {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
  } catch (_) { /* ignore */ }
});

export default router;
